import { useCallback } from "react";
import { MessageDocuments } from "@/components/chat/MessageDocuments";
import { MessageImages } from "@/components/chat/MessageImages";
import { StatusDot } from "@/components/common/StatusDot";
import { RelativeTimestamp } from "@/components/chat/RelativeTimestamp";
import { useChatStore } from "@/stores/useChatStore";
import { useToastStore } from "@/stores/useToastStore";
import {
	deleteQueuedChatMessage,
	listChatMessages,
	listQueuedChatMessages,
	QueuedMessageAlreadyPromotedError,
} from "@/lib/api";
import { extractContentText, parseMessageControls } from "@/lib/message-controls";
import {
	isDocumentContent,
	isImageContent,
	isUploadedFileContent,
	type QueuedChatMessageDto,
} from "@/lib/chat";
import { fromUploadedFileBlock } from "@/lib/uploaded-file-block";

/** 送信待ちメッセージの表示用テキスト (制御行を除いた text ブロック連結)。 */
function queuedText(entry: QueuedChatMessageDto): string {
	return parseMessageControls(extractContentText(entry.content)).text;
}

/**
 * アクティブジョブ中に送信されてキュー化された「送信待ち」メッセージを、トランスクリプトとは
 * 別のエリアに表示する。バブルは減光し、直下のフッターに「未読」ステータスと送信時刻を出す。
 * 各項目は個別に取り消せる。キューが空のときは何も描画しない。
 */
export function QueuedMessages() {
	const chatId = useChatStore((s) => s.chatId);
	const queuedMessages = useChatStore((s) => s.queuedMessages);
	const removeQueuedMessage = useChatStore((s) => s.removeQueuedMessage);

	const handleDelete = useCallback(
		async (id: string): Promise<void> => {
			if (!chatId) return;
			try {
				await deleteQueuedChatMessage(chatId, id);
				removeQueuedMessage(id);
			} catch (err: unknown) {
				if (err instanceof QueuedMessageAlreadyPromotedError) {
					// 既にジョブへ取り込まれた (昇格済み)。queue と messages を取り直して整合させる。
					try {
						const [messages, queued] = await Promise.all([
							listChatMessages(chatId),
							listQueuedChatMessages(chatId),
						]);
						if (useChatStore.getState().chatId === chatId) {
							const knownRequestIds = new Set(
								messages.map((m) => m.clientRequestId).filter((v): v is string => v != null),
							);
							useChatStore.getState().setMessages(messages);
							useChatStore
								.getState()
								.setQueuedMessages(queued.filter((q) => !knownRequestIds.has(q.clientRequestId)));
						}
					} catch (resyncErr: unknown) {
						useToastStore.getState().show({
							type: "error",
							message: "同期に失敗しました。再読み込みしてください。",
						});
						throw resyncErr;
					}
					useToastStore.getState().show({ type: "info", message: "既に取り込まれています。" });
					return;
				}
				useToastStore.getState().show({
					type: "error",
					message: "送信待ちメッセージの削除に失敗しました。",
				});
				throw err;
			}
		},
		[chatId, removeQueuedMessage],
	);

	if (queuedMessages.length === 0) return null;

	return (
		<div
			data-testid="queued-messages"
			style={{
				display: "flex",
				flexDirection: "column",
				gap: 6,
				paddingTop: 12,
				paddingBottom: 8,
			}}
		>
			{queuedMessages.map((entry) => {
				const text = queuedText(entry);
				const images = entry.content.filter(isImageContent);
				const documents = entry.content.filter(isDocumentContent);
				const uploadedFiles = entry.content
					.filter(isUploadedFileContent)
					.map(fromUploadedFileBlock);
				return (
					<div
						key={entry.id}
						style={{ display: "flex", flexDirection: "column", alignItems: "flex-end" }}
					>
						<div
							className="select-text"
							style={{
								background: "var(--surface-1)",
								borderRadius: "var(--radius-6)",
								padding: "8px 14px",
								maxWidth: "75%",
								fontSize: "var(--text-ui)",
								lineHeight: "var(--leading-body)",
								color: "var(--fg-1)",
								fontStyle: "italic",
								opacity: 0.6,
							}}
						>
							<MessageImages images={images} />
							<MessageDocuments documents={documents} uploadedFiles={uploadedFiles} />
							{text.length > 0 && <p className="whitespace-pre-wrap wrap-anywhere my-0">{text}</p>}
						</div>
						<div
							className="flex items-center justify-end gap-2 mt-1"
							title="未読: エージェントがまだ読み込んでいません。次のツール区切りかターン終了時に取り込まれます。"
						>
							<StatusDot tone="warning" pulse testId="queued-message-status" state="unread" />
							<span style={{ fontSize: "var(--text-caption)", color: "var(--fg-3)" }}>未読</span>
							<RelativeTimestamp
								iso={entry.createdAt}
								style={{
									fontSize: "var(--text-section)",
									color: "var(--fg-3)",
								}}
							/>
							<button
								type="button"
								data-testid="queued-message-delete"
								title="送信待ちを取り消す"
								onClick={() => {
									handleDelete(entry.id).catch(() => {});
								}}
								style={{
									flexShrink: 0,
									border: "none",
									background: "transparent",
									color: "var(--fg-3)",
									fontSize: "var(--text-caption)",
									cursor: "pointer",
									padding: "2px 4px",
								}}
							>
								取り消す
							</button>
						</div>
					</div>
				);
			})}
		</div>
	);
}
